import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight } from "lucide-react";
import DemoNotice from "@/components/site/DemoNotice";

/**
 * Premium, reusable page hero for non-home pages.
 * Variants:
 *   - "navy"   → dark navy/cyan gradient (services, demo)
 *   - "light"  → soft brand gradient on light bg (about, areas, services overview, service detail)
 */
export default function PageHero({
  eyebrow,
  title,
  highlight,
  subtitle,
  image,
  imageAlt = "",
  primaryCta,
  secondaryCta,
  variant = "light",
  breadcrumb,
  showDemoNotice = true,
  inlineDemoNoticeLabel,
  testId = "page-hero",
}) {
  const isNavy = variant === "navy";
  return (
    <section
      data-testid={testId}
      aria-labelledby="page-hero-heading"
      className={`relative overflow-hidden ${
        isNavy ? "bg-navy text-white" : "bg-white text-navy"
      }`}
    >
      {/* background layers */}
      {isNavy ? (
        <>
          <div className="absolute inset-0 bg-brand-gradient" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-[0.18] mix-blend-screen"
            style={{
              backgroundImage:
                "radial-gradient(circle at 18% 30%, rgba(34,211,238,0.35) 0%, transparent 45%), radial-gradient(circle at 88% 78%, rgba(245,158,11,0.16) 0%, transparent 50%)",
            }}
            aria-hidden="true"
          />
        </>
      ) : (
        <>
          <div className="absolute inset-0 bg-brand-gradient-soft" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-80"
            style={{
              backgroundImage:
                "radial-gradient(circle at 8% 18%, rgba(34,211,238,0.16) 0%, transparent 48%), radial-gradient(circle at 92% 88%, rgba(245,158,11,0.10) 0%, transparent 55%)",
            }}
            aria-hidden="true"
          />
        </>
      )}
      <div className="absolute inset-x-0 top-0 h-px bg-sun-stripe opacity-70" aria-hidden="true" />

      <div className="container-page relative grid grid-cols-1 items-center gap-10 py-16 sm:py-20 lg:grid-cols-12 lg:gap-10 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className={image ? "lg:col-span-7" : "lg:col-span-12"}
        >
          {breadcrumb && (
            <nav
              aria-label="Breadcrumb"
              className={`mb-4 flex items-center gap-1 text-xs font-medium ${
                isNavy ? "text-white/70" : "text-navy/60"
              }`}
            >
              {breadcrumb.map((crumb, i) => (
                <span key={`${crumb.label}-${i}`} className="flex items-center gap-1">
                  {crumb.to ? (
                    <Link
                      to={crumb.to}
                      className={`transition-colors ${
                        isNavy ? "hover:text-aqua" : "hover:text-aqua-deep"
                      }`}
                    >
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className={isNavy ? "text-white" : "text-navy"}>
                      {crumb.label}
                    </span>
                  )}
                  {i < breadcrumb.length - 1 && (
                    <ChevronRight className="h-3 w-3 opacity-50" />
                  )}
                </span>
              ))}
            </nav>
          )}

          {eyebrow && (
            <span className={isNavy ? "pill-dark" : "pill"}>
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  isNavy ? "bg-sun" : "bg-aqua"
                }`}
              />
              {eyebrow}
            </span>
          )}

          <h1
            id="page-hero-heading"
            className={`mt-5 font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl ${
              isNavy ? "text-white" : "text-navy"
            }`}
          >
            {title}
            {highlight && (
              <span className={`block ${isNavy ? "text-aqua" : "text-aqua-deep"}`}>
                {highlight}
              </span>
            )}
          </h1>

          {subtitle && (
            <p
              className={`mt-5 max-w-2xl text-base sm:text-lg ${
                isNavy ? "text-white/80" : "text-navy/70"
              }`}
            >
              {subtitle}
            </p>
          )}

          {(primaryCta || secondaryCta) && (
            <div className="mt-7 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              {primaryCta && (
                <Link
                  to={primaryCta.to}
                  data-testid="page-hero-cta-primary"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-sun px-6 py-3 text-sm font-semibold text-navy-deep shadow-sunGlow transition-transform hover:scale-[1.02]"
                >
                  {primaryCta.label}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              )}
              {secondaryCta && (
                <Link
                  to={secondaryCta.to}
                  data-testid="page-hero-secondary-cta"
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold shadow-soft transition-colors ${
                    isNavy
                      ? "bg-white/95 text-navy hover:bg-white"
                      : "bg-navy text-white hover:bg-navy-soft"
                  }`}
                >
                  {secondaryCta.label}
                </Link>
              )}
            </div>
          )}

          {showDemoNotice && (
            <div className="mt-6">
              <DemoNotice
                variant={isNavy ? "light" : "dark"}
                label={
                  inlineDemoNoticeLabel ||
                  "Demo site — built by PrismForge Digital · not a real HVAC company"
                }
              />
            </div>
          )}
        </motion.div>

        {image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
            className="lg:col-span-5"
          >
            <div
              className={`relative overflow-hidden rounded-3xl border shadow-lift ${
                isNavy ? "border-white/10" : "border-mist-line"
              }`}
            >
              <img
                src={image}
                alt={imageAlt}
                className="h-full max-h-[440px] w-full object-cover sm:max-h-[480px]"
                loading="eager"
              />
              <div
                className={`absolute inset-0 ${
                  isNavy
                    ? "bg-gradient-to-tr from-navy/55 via-navy/10 to-transparent"
                    : "bg-gradient-to-tr from-navy/30 via-transparent to-transparent"
                }`}
                aria-hidden="true"
              />
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}
